import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMenu } from "@/components/BrandMenu";
import { Sparkles, LogOut, Users, PartyPopper, Sparkle, Film, Shield, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl bg-card border border-white/5 p-6">
    <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-gold" /></div>
    <p className="font-impact text-4xl text-white leading-none">{value}</p>
    <p className="mt-2 text-sm text-foreground/50">{label}</p>
  </div>
);

const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? "—" : d.toLocaleString(); };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("accounts");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState({}); // share_code -> connected count
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/users").then((r) => setUsers(r.data)),
      api.get("/admin/events").then((r) => {
        setEvents(r.data);
        const seed = {};
        r.data.forEach((e) => { seed[e.share_code] = e.live_count || 0; });
        setLive((prev) => ({ ...seed, ...prev }));
      }),
    ]).catch((e) => {
      if (e.response?.status === 403) { toast.error("Admin access only"); navigate("/dashboard"); }
      else toast.error("Could not load admin data");
    }).finally(() => setLoading(false));
  }, [navigate]);

  const removeUser = async (u) => {
    if (u.role === "admin") return;
    if (!window.confirm(`Delete account "${u.email}"? This removes their reveals and reactions too. This can't be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setStats((s) => (s ? { ...s, users: Math.max(0, (s.users || 1) - 1), events: Math.max(0, (s.events || 0) - (u.event_count || 0)) } : s));
      toast.success("Account deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account");
    }
  };

  // Live connected-guest counts via the existing Socket.IO server.
  useEffect(() => {
    const s = io(BACKEND_URL, { path: "/api/socket.io", transports: ["polling", "websocket"] });
    s.on("connect", () => s.emit("join_admin", {}));
    s.on("presence_snapshot", (snap) => setLive((prev) => ({ ...prev, ...snap })));
    s.on("presence_update", ({ code, count }) => setLive((prev) => ({ ...prev, [code]: count })));
    return () => { s.disconnect(); };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMenu />
            <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gold/15 text-gold font-semibold"><Shield className="w-3 h-3" /> Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground/60 hidden sm:block">{user?.email}</span>
            <Button data-testid="admin-logout-btn" variant="outline" size="sm" onClick={() => { logout(); navigate("/"); }} className="rounded-full border-white/20 bg-transparent">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-2">Control room</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-10">Admin dashboard</h1>

        {loading ? (
          <p className="text-foreground/50">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <StatCard icon={Users} label="Accounts" value={stats?.users ?? 0} testid="stat-users" />
              <StatCard icon={PartyPopper} label="Reveals created" value={stats?.events ?? 0} testid="stat-events" />
              <StatCard icon={Sparkle} label="Revealed" value={stats?.revealed ?? 0} testid="stat-revealed" />
              <StatCard icon={Film} label="Reactions" value={stats?.reactions ?? 0} testid="stat-reactions" />
            </div>

            <div className="flex gap-2 mb-6">
              <button data-testid="tab-accounts" onClick={() => setTab("accounts")}
                className={`px-5 h-11 rounded-full font-semibold text-sm transition-colors ${tab === "accounts" ? "bg-gold text-black" : "bg-white/5 text-foreground/60 hover:bg-white/10"}`}>
                Accounts ({users.length})
              </button>
              <button data-testid="tab-reveals" onClick={() => setTab("reveals")}
                className={`px-5 h-11 rounded-full font-semibold text-sm transition-colors ${tab === "reveals" ? "bg-gold text-black" : "bg-white/5 text-foreground/60 hover:bg-white/10"}`}>
                Reveals ({events.length})
              </button>
            </div>

            {tab === "accounts" && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 overflow-hidden" data-testid="accounts-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-white/5 text-foreground/50 uppercase text-xs tracking-wider">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Name</th>
                        <th className="px-5 py-3 font-semibold">Email</th>
                        <th className="px-5 py-3 font-semibold">Role</th>
                        <th className="px-5 py-3 font-semibold">Reveals</th>
                        <th className="px-5 py-3 font-semibold">Joined</th>
                        <th className="px-5 py-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                          <td className="px-5 py-3 font-medium">{u.name || "—"}</td>
                          <td className="px-5 py-3 text-foreground/70">{u.email}</td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${u.role === "admin" ? "bg-gold/15 text-gold" : "bg-white/10 text-foreground/70"}`}>{u.role}</span>
                          </td>
                          <td className="px-5 py-3 text-foreground/70">{u.event_count}</td>
                          <td className="px-5 py-3 text-foreground/50">{fmtDate(u.created_at)}</td>
                          <td className="px-5 py-3 text-right">
                            {u.role === "admin" ? (
                              <span className="text-xs text-foreground/30">Protected</span>
                            ) : (
                              <button data-testid={`delete-user-btn-${u.id}`} onClick={() => removeUser(u)} title="Delete account"
                                className="inline-flex w-8 h-8 rounded-lg items-center justify-center text-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-foreground/50">No accounts yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {tab === "reveals" && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 overflow-hidden" data-testid="reveals-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-white/5 text-foreground/50 uppercase text-xs tracking-wider">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Title</th>
                        <th className="px-5 py-3 font-semibold">Host</th>
                        <th className="px-5 py-3 font-semibold">Type</th>
                        <th className="px-5 py-3 font-semibold">Scheduled</th>
                        <th className="px-5 py-3 font-semibold">Guests</th>
                        <th className="px-5 py-3 font-semibold">Live now</th>
                        <th className="px-5 py-3 font-semibold">Reactions</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e) => (
                        <tr key={e.id} data-testid={`event-row-${e.id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                          <td className="px-5 py-3 font-medium">{e.title}</td>
                          <td className="px-5 py-3 text-foreground/70">{e.host_email || "—"}</td>
                          <td className="px-5 py-3 text-foreground/70 capitalize">{e.reveal_type}</td>
                          <td className="px-5 py-3 text-foreground/50">{fmtDate(e.scheduled_at)}</td>
                          <td className="px-5 py-3 text-foreground/70">{e.max_guests}</td>
                          <td className="px-5 py-3" data-testid={`live-count-${e.id}`}>
                            {(live[e.share_code] || 0) > 0 ? (
                              <span className="inline-flex items-center gap-1.5 text-forest font-semibold"><span className="w-2 h-2 rounded-full bg-forest animate-pulse" />{live[e.share_code]}</span>
                            ) : (
                              <span className="text-foreground/40">0</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-foreground/70">{e.reaction_count}</td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${e.revealed ? "bg-forest/15 text-forest" : "bg-white/10 text-foreground/70"}`}>{e.revealed ? "Revealed" : "Pending"}</span>
                          </td>
                        </tr>
                      ))}
                      {events.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-foreground/50">No reveals yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
